/**
 * enginePreProcessJson_Helper.ts - Helper methods for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonValue, JsonValueType, JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { TemplateOperation as TemplateOperationType } from '../../../../Assembler/dist/model/templateOperation.js';
import type { EnginePreProcessJson } from './enginePreProcessJson.js';
import type { InheritanceOperation } from '../../../../Assembler/dist/model/inheritanceOperation.js';
import type { OperationsMetadata } from '../../../../Assembler/dist/model/operationsMetadata.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';

/**
 * Represents a template info node in the metadata hierarchy
 */
export interface TemplateInfoNode {
    readonly templateName: string;
    readonly templateAppSite: string;
    readonly placeholderName?: string;
    readonly components?: TemplateInfoNode[];
    readonly appViewPlaceholders?: Map<string, string>;
    readonly jsonInheritKeys?: Map<string, string>;
    readonly entries?: TemplateEntryInfoNode[];
}

export interface TemplateEntryInfoNode {
    readonly htmlJsonKey: string;
    readonly entryType: string;
    readonly name: string;
    readonly ordinal: number;
    readonly isSkipMarked: boolean;
    readonly hasNestedContent: boolean;
}

/**
 * Represents the metadata structure for an AppSite
 */
export interface AppSiteMetadataRef {
    readonly appFile: string;
    readonly appView: string;
    readonly appViews?: string[];
    readonly rootComponent: TemplateInfoNode | null;
}

/**
 * Result from loader.getTemplate for JSON engines
 */
export interface GetTemplateJsonResult {
    htmlJson: PlainJsonObject | null;
    dataJson: JsonObject | null;
    resolvedTemplateName: string;
    resolvedAppSite: string;
    templateInfo: TemplateInfoNode | null;
}

// Re-export types from Assembler model for use across engine files
import { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';
import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

export { TemplateOperation, TemplateOperationKind };
export type { OperationsMetadata } from '../../../../Assembler/dist/model/operationsMetadata.js';

/**
 * Creates a TemplateOperation from a plain object literal.
 * Preserves exact JS logic where plain objects were used.
 */
export function op(params: { templateName: string; kind: TemplateOperationKind; target: string; replacedWith: string; operations: TemplateOperation[] }): TemplateOperation {
    return new TemplateOperation(params);
}

/**
 * Finds the actual key in an object using case-insensitive matching.
 */
function findKeyCaseInsensitive(obj: PlainJsonMap | PlainJsonObject | null, key: string): string | null {
    if (!obj) return null;
    const keyLower = key.toLowerCase();
    if (obj instanceof Map) {
        for (const k of obj.keys()) {
            if (k.toLowerCase() === keyLower) return k;
        }
    } else {
        for (const k of Object.keys(obj)) {
            if (k.toLowerCase() === keyLower) return k;
        }
    }
    return null;
}

/**
 * Checks if an object (JsonObject/Map or plain object) has a key.
 * Uses case-insensitive fallback to match Normal engine behavior.
 */
export function hasKey(obj: JsonObject | PlainJsonMap | PlainJsonObject | null, key: string): boolean {
    if (obj instanceof Map) {
        if (obj.has(key)) return true;
    } else if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
        return true;
    }
    return findKeyCaseInsensitive(obj, key) !== null;
}

/**
 * Unwraps a JsonValue to get the raw value
 */
export function unwrapJsonValue(jsonValue: JsonValue): unknown {
    switch (jsonValue.typeName) {
        case JsonValueType.String:
            return jsonValue.asString() ?? null;
        case JsonValueType.Number:
            return jsonValue.asNumber() ?? null;
        case JsonValueType.Integer:
            return jsonValue.asInteger() ?? null;
        case JsonValueType.Bool:
            return jsonValue.asBool() ?? null;
        case JsonValueType.Array:
            return jsonValue.asArray() ?? null;
        case JsonValueType.Object:
            return jsonValue.asObject() ?? null;
        case JsonValueType.Null:
            return null;
        default:
            return null;
    }
}

/**
 * Gets a value from an object (JsonObject/Map or plain object)
 * If the value is a JsonValue, returns the raw value
 */
export function getValue(obj: JsonObject | PlainJsonMap | PlainJsonObject | null, key: string): unknown {
    let value: unknown;
    if (obj instanceof Map) {
        // Use has() before get() because JsonObject.get() returns JsonValue.null()
        // instead of undefined for missing keys, which would prevent case-insensitive fallback
        if (obj.has(key)) {
            value = obj.get(key);
        } else {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = obj.get(actualKey);
        }
    } else {
        value = obj ? (obj as PlainJsonObject)[key] : undefined;
        if (value === undefined && obj) {
            const actualKey = findKeyCaseInsensitive(obj, key);
            if (actualKey !== null) value = (obj as PlainJsonObject)[actualKey];
        }
    }

    // Unwrap JsonValue to get raw value
    if (value instanceof JsonValue) {
        return unwrapJsonValue(value);
    }
    return value;
}

/**
 * Gets all keys from an object (JsonObject/Map or plain object)
 */
export function getKeys(obj: JsonObject | PlainJsonMap | PlainJsonObject | null): string[] {
    if (obj instanceof Map) {
        return Array.from(obj.keys());
    }
    return obj ? Object.keys(obj) : [];
}

/**
 * Finds a template in the hierarchy by TemplateName
 * Used for JSON inheritance to find source template and get its TemplateAppSite
 */
function findTemplateInHierarchyImpl(root: TemplateInfoNode | null, templateName: string): TemplateInfoNode | null {
    if (!root) {
        return null;
    }

    // Check if root itself matches
    if (root.templateName && root.templateName.toLowerCase() === templateName.toLowerCase()) {
        return root;
    }

    // Search in the components of this template
    if (root.components) {
        for (const comp of root.components) {
            if (comp.templateName && comp.templateName.toLowerCase() === templateName.toLowerCase()) {
                return comp;
            }

            // Recursively search in nested components
            const found = findTemplateInHierarchyImpl(comp, templateName);
            if (found) {
                return found;
            }
        }
    }

    return null;
}

/**
 * Returns keys as array preserving insertion order
 * Keys have numeric prefix (001_, 002_) for ordering
 */
export function getKeysList(keys: string[]): string[] {
    return [...keys]; // Preserve insertion order
}

/**
 * Recursively fixes templateName in operations to use correct slot template key
 */
export function fixOperationTemplateNames(operations: TemplateOperationType[], oldTemplateKey: string, newTemplateKey: string): void {
    if (!operations || !Array.isArray(operations)) return;

    for (const op of operations) {
        // Fix templateName if it matches the old key
        if (op.templateName === oldTemplateKey) {
            op.templateName = newTemplateKey;
        }
        // Recursively fix nested operations
        if (op.operations && Array.isArray(op.operations)) {
            fixOperationTemplateNames(op.operations, oldTemplateKey, newTemplateKey);
        }
    }
}

/**
 * Converts value to string
 */
export function convertToString(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    // Handle JsonValue wrappers - unwrap to get actual value
    if (value instanceof JsonValue) {
        if (value.typeName === JsonValueType.String) {
            return value.asString() ?? '';
        } else if (value.typeName === JsonValueType.Bool) {
            return value.asBool() ? 'true' : 'false';
        } else if (value.typeName === JsonValueType.Integer) {
            return (value.asInteger() ?? 0).toString();
        } else if (value.typeName === JsonValueType.Number) {
            return (value.asNumber() ?? 0).toString();
        } else if (value.typeName === JsonValueType.Null) {
            return 'null';
        }
        // For Array and Object types, fall through to String()
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    // Match C# behavior: use ToString() equivalent, not JSON serialization
    return String(value);
}

/**
 * Evaluates a conditional expression based on data
 */
function evaluateConditionImpl(conditionalKey: string, data: JsonObject | PlainJsonMap | PlainJsonObject | null): boolean {
    // Case-insensitive key lookup
    if (data) {
        for (const key of getKeys(data)) {
            if (key.toLowerCase() === conditionalKey.toLowerCase()) {
                return convertToBoolean(getValue(data, key));
            }
        }
    }
    return false; // Key not found - default to false
}

/**
 * Evaluates a conditional key with CONTEXT PRIORITY (mirrors csharp EnginePreProcessJson_Merge_Conditional /
 * Normal's BuildMergedJson Step 3: contextJson always wins). Without this, a slot like
 * "IsAuthenticated":"{{IsAuthenticated}}" is evaluated as the unresolved placeholder string (truthy)
 * instead of the real context bool, so the JSON engine renders {{?Key}}/{{^Key}} blocks differently
 * than the Normal/PreProcess engines.
 */
export function evaluateConditionWithContext(conditionalKey: string, contextJson: JsonObject | null, data: JsonObject | PlainJsonMap | PlainJsonObject | null): boolean {
    if (contextJson) {
        for (const key of getKeys(contextJson)) {
            if (key.toLowerCase() === conditionalKey.toLowerCase()) {
                return convertToBoolean(getValue(contextJson, key));
            }
        }
    }
    return evaluateConditionImpl(conditionalKey, data);
}

/**
 * Converts value to boolean
 */
export function convertToBoolean(value: unknown): boolean {
    if (value === null || value === undefined)
        return false;

    // Handle JsonValue wrappers
    if (value instanceof JsonValue) {
        if (value.typeName === JsonValueType.Bool || value.typeName === JsonValueType.Integer || value.typeName === JsonValueType.Number)
            return value.toBool();
        // Array truthiness: non-empty = true
        if (value.typeName === JsonValueType.Array)
            return (value.asArray() ?? []).length > 0;
        // Object: exists = true
        if (value.typeName === JsonValueType.Object)
            return true;
        // String: non-empty and not "false"/"0"
        if (value.typeName === JsonValueType.String) {
            const s = value.asString() ?? '';
            return s.length > 0 &&
                   s.toLowerCase() !== 'false' &&
                   s.toLowerCase() !== '0';
        }
        return false;
    }

    if (typeof value === 'boolean')
        return value;

    if (typeof value === 'string') {
        // node decodes PlainJsonObject members to raw JS values, so this branch carries the
        // population csharp handles in its JsonValue-String branch (non-empty, not "false"/"0").
        // csharp's raw `value is string` bool.TryParse branch is unreachable there — its data
        // model always boxes into JsonValue — so mirroring it here would drop live conditionals.
        return value.length > 0 &&
               value.toLowerCase() !== 'false' &&
               value.toLowerCase() !== '0';
    }

    if (typeof value === 'number')
        return value !== 0;

    // Array truthiness: non-empty = true
    if (Array.isArray(value))
        return value.length > 0;

    // Object: exists = true
    if (typeof value === 'object')
        return true;

    return false;
}

export function convertDynamicViewResults(dynamicViewResults: JsonObject): Record<string, string> {
    const dynamicView: Record<string, string> = {};

    for (const [key, value] of dynamicViewResults.entries()) {
        if (value.isObject) {
            const html = GenerateHtmlJson.convertJsonToHtml(value.getObject());
            dynamicView[key] = CommonUtil.normalizeFileContent(html);
        } else if (value.isString) {
            dynamicView[key] = value.getString();
        }
    }

    return dynamicView;
}

export const helperMethods = {
    findTemplateInHierarchy(this: EnginePreProcessJson, root: TemplateInfoNode | null, templateName: string): TemplateInfoNode | null {
        if (!root) {
            return null;
        }

        if (root.templateName && root.templateName.toLowerCase() === templateName.toLowerCase()) {
            return root;
        }

        if (root.components) {
            for (const comp of root.components) {
                if (comp.templateName && comp.templateName.toLowerCase() === templateName.toLowerCase()) {
                    return comp;
                }

                const found = findTemplateInHierarchyImpl(comp, templateName);
                if (found) {
                    return found;
                }
            }
        }

        return null;
    },

    containsNumberPattern(this: EnginePreProcessJson, placeholderName: string): boolean {
        if (!placeholderName) {
            return false;
        }

        const parts = placeholderName.split('_');
        if (parts.length < 3) {
            return false;
        }

        for (let i = 1; i < parts.length - 1; i++) {
            if (!isNaN(parseInt(parts[i]))) {
                return true;
            }
        }

        return false;
    },

    evaluateCondition(this: EnginePreProcessJson, conditionalKey: string, data: JsonObject | PlainJsonMap | PlainJsonObject | null): boolean {
        const keys = getKeys(data);
        for (const key of keys) {
            if (key.toLowerCase() === conditionalKey.toLowerCase()) {
                return convertToBoolean(getValue(data, key));
            }
        }

        return false;
    },

    createFlowStack(this: EnginePreProcessJson, appFile: string): string[] {
        return [appFile];
    },

    buildOperationsMetadata(
        this: EnginePreProcessJson,
        genOperationJson: boolean,
        appSite: string,
        appFile: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperationType[],
        inheritanceOperations: InheritanceOperation[]
    ): OperationsMetadata | null {
        if (!genOperationJson) {
            return null;
        }

        return {
            appSite,
            appFile,
            appView: appView || '',
            templateName: templateKey,
            operations,
            hasInheritance: inheritanceOperations.length > 0,
            inheritanceOperations
        } as OperationsMetadata;
    },

    accumulateDynamicDataTimingFromLoader(this: EnginePreProcessJson): void {
        const { nanos, count } = this._loader!.getDynamicDataTiming();
        this.accumulateDynamicDataTiming(nanos, count);
    },

    accumulateDynamicViewTimingFromLoader(this: EnginePreProcessJson): void {
        const { nanos, count } = this._loader!.getDynamicViewTiming();
        this.accumulateDynamicViewTiming(nanos, count);
    },

    accumulateDynamicActionTimingFromLoader(this: EnginePreProcessJson): void {
        const { nanos, count } = this._loader!.getDynamicActionTiming();
        this.accumulateDynamicActionTiming(nanos, count);
    },

    accumulateDynamicClientDataTimingFromLoader(this: EnginePreProcessJson): void {
        const { nanos, count } = this._loader!.getDynamicClientDataTiming();
        this.accumulateDynamicClientDataTiming(nanos, count);
    }
};
